import React from "react";
// ANT DESIGN COMPONENTS
import { Select, Tag } from "antd";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
// INTERFACES
import type {
  OpcUaDataKind,
  OpcUaDataValueForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

/** Human-readable labels for every authorable Data-union kind (Array is decode-only, excluded from the picker). */
const DATA_KIND_OPTIONS: { value: OpcUaDataKind; label: string }[] = [
  { value: "bool", label: "Bool" },
  { value: "sbyte", label: "SByte" },
  { value: "byte", label: "Byte" },
  { value: "u16", label: "UInt16" },
  { value: "i16", label: "Int16" },
  { value: "u32", label: "UInt32" },
  { value: "i32", label: "Int32" },
  { value: "u64", label: "UInt64" },
  { value: "i64", label: "Int64" },
  { value: "float", label: "Float" },
  { value: "double", label: "Double" },
  { value: "string", label: "String" },
  { value: "datetime", label: "DateTime" },
  { value: "guid", label: "Guid" },
  { value: "bytestring", label: "ByteString" },
  { value: "statuscode", label: "StatusCode" },
  { value: "localizedtext", label: "LocalizedText (locale;text)" },
  { value: "qualifiedname", label: "QualifiedName (ns;name)" },
  { value: "nodeid", label: "NodeId (ns;identifier)" },
  { value: "expandednodeid", label: "ExpandedNodeId (ns;identifier)" },
  { value: "xmlelement", label: "XmlElement" },
];

/** Placeholder hint text per kind — clarifies the compound-string encodings. */
const KIND_PLACEHOLDER: Partial<Record<OpcUaDataKind, string>> = {
  bool: "true / false",
  bytestring: "1,2,255",
  localizedtext: "en-US;Hello",
  qualifiedname: "2;MyName",
  nodeid: "2;1001",
  expandednodeid: "2;1001",
  u64: "0",
  i64: "0",
  datetime: "0",
};

/**
 * Shared editor for one OpcUaDataValueForm (the 22-kind Data/Variant union
 * used by node values, method arguments, and literal filter operands).
 * "array" is decode-only — when the current value is an array this
 * renders a read-only summary instead of the kind/value fields, but the
 * value round-trips unchanged (see `opcUaCdoBuilder.ts` `fbDataHolderToEditor`).
 */
export default function DataValueEditor({
  value,
  onChange,
  label = "Value",
}: {
  value: OpcUaDataValueForm;
  onChange: (value: OpcUaDataValueForm) => void;
  label?: string;
}) {
  if (value.kind === "array") {
    return (
      <div className="row">
        <div className="col-12 mt-2">
          <span className="generalStyles__inputLabel">{label} (Array — read-only)</span>
          <div style={{ marginTop: 8 }}>
            <Tag color="default">{(value.elements ?? []).length} element(s)</Tag>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12 col-md-4 mt-2">
        <span className="generalStyles__inputLabel">{label} Type</span>
        <Select
          className="generalStyles__width100"
          size="large"
          value={value.kind}
          onChange={(kind: OpcUaDataKind) => onChange({ kind, value: "" })}
        >
          {DATA_KIND_OPTIONS.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>
      <div className="col-12 col-md-8 mt-2">
        <FormInputText
          inputValue={value.value}
          inputLabelName={
            KIND_PLACEHOLDER[value.kind] ? `${label} (${KIND_PLACEHOLDER[value.kind]})` : label
          }
          inputOnChange={(next) => onChange({ ...value, value: next })}
          inputVariableName="dataValue"
          isRequired={false}
          disabledInput={false}
        />
      </div>
    </div>
  );
}
